import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { ProjectDto } from '../../../../api';
import { queryKeys } from '../../../../constants';
import useAuthenticatedClients from '../../../../hooks/useAuthenticatedClients';
import { onMutationError } from '../../../../utils/Utils';

function ProjectQueries() {
  const { PROJECTS_QUERY_KEY, CLIENTS_QUERY_KEY } = queryKeys;
  const { getProjectsClient } = useAuthenticatedClients();
  const queryClient = useQueryClient();

  const FetchProjects = (clientId: number) => {
    const result = useQuery([PROJECTS_QUERY_KEY, clientId], async () => {
      return getProjectsClient().then(client => client.getProjectsByClient(clientId));
    });

    return result;
  };

  /**
   * Updates projects when a new project is created
   */
  const { mutate: mutateCreateProject } = useMutation<ProjectDto, Error, ProjectDto>(createProject, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(PROJECTS_QUERY_KEY);
      await queryClient.invalidateQueries(CLIENTS_QUERY_KEY);
      toast.success('Successfully created project');
    },
    onError: onMutationError,
  });

  /**
   * Updates projects when an existing project is updated
   */
  const { mutate: mutateUpdateProject } = useMutation<ProjectDto, Error, ProjectDto>(updateProject, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(PROJECTS_QUERY_KEY);
      toast.success('Successfully updated project');
    },
    onError: onMutationError,
  });

  /**
   * Updates projects when a project is deleted
   */
  const { mutate: mutateDeleteProject } = useMutation<void, Error, number>(deleteProject, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(PROJECTS_QUERY_KEY);
      toast.success('Successfully deleted project');
    },
    onError: onMutationError,
  });

  /**
   * Creates a POST request to create a new project
   * @param ProjectDto Project DTO containing data to create a new Project
   */
  async function createProject(ProjectDto: ProjectDto) {
    const client = await getProjectsClient();
    const project = await client.createProject(ProjectDto);
    return project;
  }

  /**
   * Creates a PUT request to update an existing project
   * @param ProjectDto Project DTO containing data to update an existing project
   */
  async function updateProject(ProjectDto: ProjectDto) {
    const client = await getProjectsClient();
    const project = await client.updateProject(ProjectDto);
    return project;
  }

  /**
   * Creates a DELETE request to delete a project
   * @param id the id of the project to delete
   */
  async function deleteProject(id: number) {
    const client = await getProjectsClient();
    await client.deleteProject(id);
  }

  return {
    FetchProjects,
    mutateCreateProject,
    mutateUpdateProject,
    mutateDeleteProject,
  };
}

export default ProjectQueries;
